import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'

import { DetailHeaderAction } from '../models/detail-page.model'

export type DetailActionButtonFlavor = 'bootstrap' | 'material'

/**
 * Single header-style action button. Two visual flavors:
 *
 *  - `flavor="bootstrap"` (default) — Bootstrap btn-sm with FA icons.
 *    Used in detail-page headers, table cells, sidebars.
 *  - `flavor="material"` — mat-button / mat-raised-button with mat-icons
 *    and a mat-spinner busy state. Used inside `<app-oee-modal>` and
 *    other Material-themed modal action bars so the button visual
 *    matches the surrounding chrome.
 */
@Component({
    selector: 'app-detail-action-button',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detail-action-button.component.html',
    styleUrls: ['./detail-action-button.component.scss']
})
export class DetailActionButtonComponent {
    @Input({ required: true }) action!: DetailHeaderAction
    @Input() flavor: DetailActionButtonFlavor = 'bootstrap'
    @Output() clicked = new EventEmitter<DetailHeaderAction>()

    onClick(): void {
        if (this.action.disabled || this.action.loading) return
        this.clicked.emit(this.action)
    }

    get isDisabled(): boolean {
        return !!(this.action.disabled || this.action.loading)
    }

    get tooltipText(): string {
        if (this.isDisabled && this.action.disabledReason) return this.action.disabledReason
        return this.action.tooltip || ''
    }

    btnClass(tone: string | undefined): string {
        switch (tone) {
            case 'primary': return 'btn btn-sm btn-primary'
            case 'success': return 'btn btn-sm btn-success'
            case 'danger':  return 'btn btn-sm btn-outline-danger'
            case 'warning': return 'btn btn-sm btn-outline-warning'
            case 'info':    return 'btn btn-sm btn-outline-info'
            default:        return 'btn btn-sm btn-outline-secondary'
        }
    }

    matColor(tone: string | undefined): 'primary' | 'accent' | 'warn' | undefined {
        switch (tone) {
            case 'primary': return 'primary'
            case 'success': return 'primary'
            case 'info':    return 'accent'
            case 'danger':  return 'warn'
            case 'warning': return 'warn'
            default:        return undefined
        }
    }

    /** Primary, success, and danger tones get the raised (filled)
     *  Material button. Secondary, info, warning render flat. */
    get isMatRaised(): boolean {
        const t = this.action.tone
        return t === 'primary' || t === 'success' || t === 'danger'
    }

    /** Lets a success-toned mat-button still get the green Bootstrap
     *  color override that the legacy modals were using. */
    get matExtraClass(): string {
        return this.action.tone === 'success' ? 'btn-success' : ''
    }

    get materialIconName(): string | null {
        return this.action.matIcon ?? null
    }
}
